import { memo, useEffect, useMemo, useState } from 'react'
import { RadarChart } from '@mantine/charts'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Flame,
  Target,
  TimerReset,
  Trophy,
  Zap,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import api from '../../api/client'
import { DashboardDomainRadarDatum, dashboardApi, StudentDashboardSummary } from '../../api/dashboard'
import { assignmentsApi, StudentAssignmentItem } from '../../api/assignments'

/* ------------------------------------------------------------------ */
/*  Brand palette (matches Home.tsx)                                   */
/* ------------------------------------------------------------------ */
const BRAND = {
  grapefruit: 'var(--sat-red)',
  eveningSea: 'var(--sat-green)',
  lemon:      'var(--sat-yellow)',
  blush:      'var(--sat-blush)',
  sunkenShip: 'var(--sat-text)',
  background: 'var(--sat-bg)',
}

function useDashColors() {
  return {
    text:        'var(--sat-text)',
    textDim:     'var(--sat-text-dim)',
    textMuted:   'var(--sat-text-muted)',
    surface:     'var(--sat-surface)',
    surfaceDim:  'var(--sat-surface-dim)',
    border:      'var(--sat-border)',
    shadow:      'var(--sat-shadow)',
    red:         BRAND.grapefruit,
    green:       BRAND.eveningSea,
    heroGrad:    'var(--sat-hero-gradient)',
    heroBubble:  'var(--sat-hero-bubble)',
    radarGrid:   'var(--sat-radar-grid)',
    radarFill:   'var(--sat-radar-fill)',
    radarStroke: BRAND.eveningSea,
    radarLine:   'var(--sat-radar-line)',
    countdownBg: 'var(--sat-surface-dim)',
  }
}

/* ------------------------------------------------------------------ */
/*  Static data                                                         */
/* ------------------------------------------------------------------ */
const DEFAULT_SECTION_PROGRESS = [
  { label: 'R&W',  current: 0, target: 610, color: BRAND.eveningSea },
  { label: 'Math', current: 0, target: 700, color: BRAND.grapefruit },
]

const scheduleItems = [
  { type: 'Drill',      title: 'Text Structure and Purpose',             accent: BRAND.grapefruit, bg: 'var(--sat-red-soft-strong)', href: '/practice' },
  { type: 'Vocabulary', title: 'Makon Level 3 — 20 questions',           accent: BRAND.lemon,      bg: 'var(--sat-yellow-soft)', href: '/vocabulary' },
  { type: 'Quiz',       title: 'Understanding Text Structure in Writing', accent: BRAND.eveningSea, bg: 'var(--sat-green-soft-strong)', href: '/study-plan' },
]

const topicColumns = {
  math:    ['Heart of Algebra', 'Advanced Math', 'Problem Solving'],
  reading: ['Information and Ideas', 'Craft and Structure', 'Expression of Ideas'],
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */
function clampPercent(value: number, total: number) {
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)))
}

function getCountdown(targetDate: Date) {
  const diff = Math.max(targetDate.getTime() - Date.now(), 0)
  const total = Math.floor(diff / 1000)
  return [
    { label: 'Days',    value: Math.floor(total / 86400) },
    { label: 'Hours',   value: Math.floor((total % 86400) / 3600) },
    { label: 'Minutes', value: Math.floor((total % 3600) / 60) },
    { label: 'Seconds', value: total % 60 },
  ]
}

const EMPTY_COUNTDOWN = [
  { label: 'Days', value: 0 },
  { label: 'Hours', value: 0 },
  { label: 'Minutes', value: 0 },
  { label: 'Seconds', value: 0 },
] as const

function parseUserSatDate(sat: string | null | undefined): Date | null {
  if (!sat) return null
  const d = new Date(sat)
  return Number.isNaN(d.getTime()) ? null : d
}

function isoDateKey(d: Date) {
  return d.toISOString().slice(0, 10)
}

function formatRadarLabel(label: string) {
  return label.length > 15 ? `${label.slice(0, 15)}...` : label
}

function getDomainRadarAccuracy(item: DashboardDomainRadarDatum) {
  const total = item.correct_count + item.wrong_count
  if (total <= 0) return 0
  return (item.correct_count / total) * 100
}

type DashboardRadarTooltipPayload = {
  domainFull?: string
  accuracy?: number
  correct?: number
  wrong?: number
}

function DashboardRadarTooltip({
  active,
  payload,
}: {
  active?: boolean
  /** Recharts passes `ReadonlyArray` (`TooltipPayload`); keep compatible with that. */
  payload?: ReadonlyArray<{ payload?: DashboardRadarTooltipPayload }>
}) {
  const colors = useDashColors()
  const row = payload?.[0]?.payload

  if (!active || !row) return null

  return (
    <Paper
      radius="md"
      p="sm"
      shadow="sm"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack gap={4}>
        <Text fw={700} c={colors.text}>{row.domainFull ?? 'Domain'}</Text>
        <Text fz="sm" c={colors.textMuted}>
          Accuracy: {(row.accuracy ?? 0).toFixed(1)}%
        </Text>
        <Text fz="sm" c={colors.green}>Correct: {row.correct ?? 0}</Text>
        <Text fz="sm" c={colors.red}>Wrong: {row.wrong ?? 0}</Text>
      </Stack>
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  Radar chart                                                          */
/* ------------------------------------------------------------------ */
const DashboardRadarChart = memo(function DashboardRadarChart({ data }: { data: DashboardDomainRadarDatum[] }) {
  const colors = useDashColors()
  const chartData = useMemo(
    () => data.map((item) => ({
      domain: formatRadarLabel(item.domain_name),
      domainFull: item.domain_name,
      accuracy: Number(getDomainRadarAccuracy(item).toFixed(1)),
      correct: item.correct_count,
      wrong: item.wrong_count,
    })),
    [data]
  )

  return (
    <RadarChart
      h={260}
      w="100%"
      miw={0}
      data={chartData}
      dataKey="domain"
      series={[
        {
          name: 'accuracy',
          color: 'eveningSea.6',
          strokeColor: 'eveningSea.7',
          opacity: 0.24,
          label: 'Accuracy',
        },
      ]}
      gridColor={colors.radarGrid}
      textColor={colors.textMuted}
      withPolarGrid
      withPolarAngleAxis
      withPolarRadiusAxis
      withTooltip
      polarAngleAxisProps={{
        tick: { fontSize: 10 },
      }}
      polarRadiusAxisProps={{
        domain: [0, 100],
      }}
      tooltipProps={{
        content: ({ active, payload }) => (
          <DashboardRadarTooltip active={active} payload={payload} />
        ),
      }}
    />
  )
})

function CountdownCard({
  examDate,
  examDateLabel,
}: {
  examDate: Date | null
  examDateLabel: string | null
}) {
  const colors = useDashColors()
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!examDate) return
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [examDate])

  const countdown = useMemo(() => {
    if (!examDate) return [...EMPTY_COUNTDOWN]
    return getCountdown(examDate)
  }, [examDate, tick])

  return (
    <MetricCard title="Exam countdown" icon={<CalendarDays size={20} />}>
      <Stack gap="md">
        <SimpleGrid cols={4} spacing="sm">
          {countdown.map((item) => (
            <Paper
              key={item.label}
              radius="lg"
              p="sm"
              ta="center"
              style={{
                background: colors.countdownBg,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Text fz={34} fw={800} lh={1} c={colors.text}>
                {String(item.value).padStart(2, '0')}
              </Text>
              <Text fz={10} fw={700} tt="uppercase" c={colors.textMuted}>
                {item.label}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
        {examDateLabel ? (
          <Text ta="center" c={colors.textDim}>Exam date: {examDateLabel}</Text>
        ) : (
          <Text ta="center" fz="sm" c={colors.textMuted}>
            Set your SAT test date during onboarding (or it will appear here once saved on your account).
          </Text>
        )}
      </Stack>
    </MetricCard>
  )
}

/* ------------------------------------------------------------------ */
/*  MetricCard                                                          */
/* ------------------------------------------------------------------ */
function MetricCard({
  title, icon, children, rightSection,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  rightSection?: React.ReactNode
}) {
  const colors = useDashColors()
  return (
    <Paper
      radius="xl"
      p="lg"
      withBorder
      style={{
        background:   colors.surface,
        borderColor:  colors.border,
        boxShadow:    `0 18px 40px ${colors.shadow}`,
        height: '100%',
      }}
    >
      <Group justify="space-between" align="flex-start" mb="md">
        <Group gap="sm">
          <ThemeIcon
            size={42}
            radius="xl"
            variant="light"
            color="grapefruit"
            style={{ background: 'var(--sat-red-soft-strong)', color: colors.red }}
          >
            {icon}
          </ThemeIcon>
          <Text fw={700} c={colors.text}>{title}</Text>
        </Group>
        {rightSection}
      </Group>
      {children}
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  Dashboard                                                           */
/* ------------------------------------------------------------------ */
export function StudentDashboard() {
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const colors = useDashColors()

  const [assignments, setAssignments] = useState<StudentAssignmentItem[]>([])
  const [assignLoading, setAssignLoading] = useState(true)
  const [dashboardSummary, setDashboardSummary] = useState<StudentDashboardSummary | null>(null)

  const [targetRw, setTargetRw] = useState<number | string>('')
  const [targetMath, setTargetMath] = useState<number | string>('')
  useEffect(() => {
    if (user && user.role === 'student' && user.onboarding_completed === false) {
      navigate('/onboarding', { replace: true })
    }
  }, [user, navigate])

  useEffect(() => {
    if (!user) return
    setTargetRw(user.target_score_rw ?? '')
    setTargetMath(user.target_score_math ?? '')
  }, [user?.id, user?.target_score_rw, user?.target_score_math])

  const examDate = user?.sat_date ? parseUserSatDate(user.sat_date) : null
  const examDateLabel = examDate
    ? examDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : null

  useEffect(() => {
    assignmentsApi.list()
      .then((res) => setAssignments(res.data as StudentAssignmentItem[]))
      .catch(() => {})
      .finally(() => setAssignLoading(false))
  }, [])

  useEffect(() => {
    dashboardApi.student()
      .then((res) => setDashboardSummary(res.data))
      .catch(() => {})
  }, [])

  const rwTargetNum = typeof targetRw === 'number' ? targetRw : Number(targetRw)
  const mathTargetNum = typeof targetMath === 'number' ? targetMath : Number(targetMath)
  const rwTargetDisplay = Number.isFinite(rwTargetNum) && rwTargetNum > 0 ? rwTargetNum : DEFAULT_SECTION_PROGRESS[0].target
  const mathTargetDisplay = Number.isFinite(mathTargetNum) && mathTargetNum > 0 ? mathTargetNum : DEFAULT_SECTION_PROGRESS[1].target
  const totalTargetScore = rwTargetDisplay + mathTargetDisplay
  const latestRw = dashboardSummary?.latest_rw_score ?? null
  const latestMath = dashboardSummary?.latest_math_score ?? null
  const totalActualScore = (latestRw ?? 0) + (latestMath ?? 0)
  const sectionsForProgress = [
    { ...DEFAULT_SECTION_PROGRESS[0], current: latestRw ?? 0, target: rwTargetDisplay },
    { ...DEFAULT_SECTION_PROGRESS[1], current: latestMath ?? 0, target: mathTargetDisplay },
  ]
  const hasEvalScore = latestRw !== null || latestMath !== null

  const saveTargets = async () => {
    const payload: {
      target_score_rw?: number
      target_score_math?: number
    } = {}
    if (targetRw !== '' && targetRw !== undefined && targetRw !== null) {
      const n = Number(targetRw)
      if (!Number.isNaN(n)) payload.target_score_rw = n
    }
    if (targetMath !== '' && targetMath !== undefined && targetMath !== null) {
      const n = Number(targetMath)
      if (!Number.isNaN(n)) payload.target_score_math = n
    }
    if (Object.keys(payload).length === 0) return
    try {
      await api.put('/auth/me', payload)
      await refreshUser()
    } catch {
      /* non-fatal */
    }
  }

  const activeAssignments = assignments.filter(
    (a) => a.assignment && (a.status === 'assigned' || a.status === 'processing')
  )
  const recentGraded = assignments
    .filter((a) => a.assignment && (a.status === 'submitted' || a.status === 'graded' || a.status === 'reviewed'))
    .slice(0, 3)
  const recentActivitySet = useMemo(
    () => new Set(dashboardSummary?.recent_activity_dates ?? []),
    [dashboardSummary?.recent_activity_dates]
  )
  const weekActivity = useMemo(() => {
    const today = new Date()
    const start = new Date(today)
    start.setHours(0, 0, 0, 0)
    start.setDate(start.getDate() - 6)

    return Array.from({ length: 7 }, (_, index) => {
      const current = new Date(start)
      current.setDate(start.getDate() + index)
      const key = isoDateKey(current)
      const isToday = key === isoDateKey(new Date())
      return {
        key,
        label: current.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2).toUpperCase(),
        isActive: recentActivitySet.has(key),
        isToday,
      }
    })
  }, [recentActivitySet])
  const dayStreak = dashboardSummary?.day_streak ?? 0
  const streakMessage = dashboardSummary?.streak_active_today
    ? 'Streak active today'
    : dayStreak > 0
      ? 'Keep the streak alive today'
      : 'Start your streak today'
  const domainRadarData = useMemo(
    () =>
      (dashboardSummary?.domain_radar ?? []).filter(
        (item) => item.correct_count > 0 || item.wrong_count > 0
      ),
    [dashboardSummary?.domain_radar]
  )

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">

        {/* ── Hero banner ── */}
        <Paper
          radius={28}
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: colors.heroGrad,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 24px 48px ${colors.shadow}`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            style={{
              position: 'absolute',
              inset: 'auto -90px -90px auto',
              width: 220, height: 220,
              borderRadius: '50%',
              background: colors.heroBubble,
              filter: 'blur(8px)',
            }}
          />

          <Group justify="space-between" align="flex-start" gap="lg" wrap="wrap" style={{ position: 'relative' }}>
            <Stack gap={8}>
              <Badge
                variant="light"
                color="grapefruit"
                radius="xl"
                leftSection={<Zap size={12} />}
                style={{ width: 'fit-content' }}
              >
                {user?.plan_code?.toUpperCase() || 'FREE'} plan
              </Badge>
              <Title order={1} style={{ color: colors.text, letterSpacing: '-0.04em' }}>
                Welcome back, {user?.user_name || 'Student'}
              </Title>
              <Text maw={620} c={colors.textDim} fz="md">
                Your SAT dashboard. Keeps the momentum.
              </Text>
            </Stack>

            <Group gap="sm">
              <Button
                radius="xl"
                color="grapefruit"
                leftSection={<BookOpen size={16} />}
                onClick={() => navigate('/study-plan')}
              >
                Start practice
              </Button>
               
            </Group>
          </Group>
        </Paper>

        <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">

          {/* Target scores — editable inputs (not MetricCard) */}
          <Paper
            radius="xl"
            p="lg"
            withBorder
            style={{
              background: colors.surface,
              borderColor: colors.border,
              boxShadow: `0 18px 40px ${colors.shadow}`,
              height: '100%',
            }}
          >
            <Group justify="space-between" align="flex-start" mb="md">
              <Group gap="sm">
                <ThemeIcon
                  size={42}
                  radius="xl"
                  variant="light"
                  color="grapefruit"
                  style={{ background: 'var(--sat-red-soft-strong)', color: colors.red }}
                >
                  <Target size={20} />
                </ThemeIcon>
                <Text fw={700} c={colors.text}>Target scores ({totalTargetScore})</Text>
              </Group>
            </Group>
            <Stack gap="md">
              <Group grow align="flex-start">
                <NumberInput
                  label="Reading & Writing target"
                  placeholder="200–800"
                  min={200}
                  max={800}
                  clampBehavior="strict"
                  value={targetRw === '' ? undefined : targetRw}
                  onChange={(v) => setTargetRw(v ?? '')}
                  onBlur={() => { void saveTargets() }}
                />
                <NumberInput
                  label="Math target"
                  placeholder="200–800"
                  min={200}
                  max={800}
                  clampBehavior="strict"
                  value={targetMath === '' ? undefined : targetMath}
                  onChange={(v) => setTargetMath(v ?? '')}
                  onBlur={() => { void saveTargets() }}
                />
              </Group>
              <Divider
                label={(
                  <Group gap={8} wrap="nowrap">
                    <Text fz="sm" fw={700} tt="uppercase" c={colors.textMuted}>
                      Latest mock score
                    </Text>
                    <Text fz={28} fw={900} lh={1} c={hasEvalScore ? colors.red : colors.textMuted}>
                      {hasEvalScore ? totalActualScore : 'N/A'}
                    </Text>
                  </Group>
                )}
                labelPosition="center"
              />
              {hasEvalScore ? sectionsForProgress.map((item) => (
                <Stack key={item.label} gap={6}>
                  <Group justify="space-between">
                    <Text fw={700} c={colors.text}>{item.label}</Text>
                    <Text fz="sm" c={colors.textMuted}>Target: {item.target}</Text>
                  </Group>
                  <Progress
                    value={clampPercent(item.current, item.target)}
                    size="lg"
                    radius="xl"
                    color={item.label === 'R&W' ? 'eveningSea' : 'grapefruit'}
                    style={{ background: 'var(--sat-neutral-soft)' }}
                  />
                  <Text ta="right" fz="sm" c={colors.textDim}>Latest score: {item.current}</Text>
                </Stack>
              )) : (
                <Text ta="center" fz="sm" c={colors.textMuted} py="sm">
                  Complete an evaluation to see your scores here.
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Progress radar */}
          <MetricCard
            title="Progress radar"
            icon={<TimerReset size={20} />}
            rightSection={
              <Badge variant="light" color="eveningSea" radius="xl">
                {domainRadarData.length} domains
              </Badge>
            }
          >
            {domainRadarData.length > 0 ? (
              <DashboardRadarChart data={domainRadarData} />
            ) : (
              <Text ta="center" py="xl" c={colors.textMuted}>
                Complete evaluations to see your real domain radar.
              </Text>
            )}
          </MetricCard>

          {/* Day streak */}
          <MetricCard title="Day streak" icon={<Flame size={20} />}>
            <Stack align="center" gap="md">
              <Text fz={52} fw={800} lh={1} style={{ color: colors.red }}>{dayStreak}</Text>
              <Text c={colors.textDim}>{streakMessage}</Text>
              <Group gap={8} wrap="nowrap">
                {weekActivity.map((day) => {
                  return (
                    <Stack key={day.key} gap={4} align="center">
                      <ThemeIcon
                        size={34}
                        radius="xl"
                        variant={day.isActive ? 'filled' : 'light'}
                        color={day.isActive ? 'grapefruit' : 'gray'}
                        style={{
                          background: day.isActive ? colors.red : 'var(--sat-neutral-soft)',
                          color: day.isActive ? 'var(--mantine-color-white)' : colors.textMuted,
                          outline: day.isToday ? `2px solid ${colors.red}` : 'none',
                          outlineOffset: 2,
                        }}
                      >
                        <Flame size={16} />
                      </ThemeIcon>
                      <Text fz={11} fw={700} c={day.isActive ? colors.red : colors.textMuted}>
                        {day.label}
                      </Text>
                    </Stack>
                  )
                })}
              </Group>
            </Stack>
          </MetricCard>
        </SimpleGrid>

        <Box mt="lg">
          <CountdownCard examDate={examDate} examDateLabel={examDateLabel} />
        </Box>
      </Stack>
    </Container>
  )
}
